import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import HistoryIcon from "@mui/icons-material/History";
import RestoreIcon from "@mui/icons-material/Restore";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";
import axios from "../lib/axios";
import moment from "moment";
import LazyTable from "./widgets/lazytable";

const AppVersionManagement = () => {
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    rowsPerPage: 5,
  });

  const [versionData, setVersionData] = useState({
    version: "",
    // platform: "all",
    forceUpdate: false,
    notes: "",
    isActive: true,
  });

  const fetchLatestVersion = async () => {
    setFetching(true);
    try {
      const response = await axios.get("/public/version/latest");
      if (response.data) {
        setVersionData({
          version: response.data.version || "",
          // platform: response.data.platform || "all",
          forceUpdate: response.data.forceUpdate || false,
          notes: response.data.notes || "",
          isActive:
            response.data.isActive !== undefined
              ? response.data.isActive
              : true,
        });
      }
    } catch (error) {
      console.error("Error fetching latest version:", error);
    } finally {
      setFetching(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await axios.get("/private/backend/version/history");
      setHistory(response.data || []);
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load version history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestVersion();
    fetchHistory();
  }, []);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setVersionData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!versionData.version) {
      toast.error("Version string is required");
      return;
    }

    setLoading(true);
    try {
      await axios.post("/private/backend/version/update", versionData);
      toast.success("App version updated successfully!");
      fetchLatestVersion();
      fetchHistory();
    } catch (error) {
      console.error("Error updating version:", error);
      toast.error(error.response?.data?.message || "Failed to update version");
    } finally {
      setLoading(false);
    }
  };

  const handleRollback = async (id) => {
    if (!window.confirm("Are you sure you want to rollback to this version?"))
      return;

    setHistoryLoading(true);
    try {
      await axios.post("/private/backend/version/rollback", { id });
      toast.success("Rollback successful!");
      fetchLatestVersion();
      fetchHistory();
    } catch (error) {
      console.error("Rollback error:", error);
      toast.error(error.response?.data?.message || "Rollback failed");
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm("Are you sure you want to delete this historical record?")
    )
      return;

    setHistoryLoading(true);
    try {
      await axios.delete(`/private/backend/version/delete?id=${id}`);
      toast.success("Version deleted");
      fetchHistory();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Delete failed");
    } finally {
      setHistoryLoading(false);
    }
  };

  const columns = [
    { field: "version", headerName: "Version", filter: true },
    /* {
      field: "platform",
      headerName: "Platform",
      render: (row) => (
        <Chip
          label={row.platform.toUpperCase()}
          size="small"
          color={row.platform === "all" ? "primary" : "secondary"}
          variant="outlined"
        />
      ),
    }, */
    {
      field: "forceUpdate",
      headerName: "Force",
      render: (row) => (
        <Chip
          label={row.forceUpdate ? "Yes" : "No"}
          size="small"
          color={row.forceUpdate ? "error" : "default"}
        />
      ),
    },
    {
      field: "isActive",
      headerName: "Status",
      render: (row) => (
        <Chip
          label={row.isActive ? "Active" : "Archived"}
          size="small"
          color={row.isActive ? "success" : "default"}
        />
      ),
    },
    {
      field: "createdAt",
      headerName: "Date",
      render: (row) => moment(row.createdAt).format("MMM DD, YYYY HH:mm"),
    },
    {
      field: "actions",
      headerName: "Actions",
      render: (row) => (
        <Box>
          {row.isActive ? (
            <Chip
              label="Current"
              size="small"
              variant="outlined"
              color="success"
            />
          ) : (
            <Box display="flex">
              <Tooltip title="Instant Rollback">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => handleRollback(row._id)}
                >
                  <RestoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Record">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => handleDelete(row._id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      ),
    },
  ];

  return (
    <Box p={3}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h5" fontWeight={600}>
          App Version Management
        </Typography>
        <Box>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchLatestVersion();
              fetchHistory();
            }}
            disabled={fetching || historyLoading}
          >
            Refresh All
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} lg={5}>
          <Typography
            variant="h6"
            gutterBottom
            display="flex"
            alignItems="center"
          >
            Publish New Version
          </Typography>
          <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Version String"
                    name="version"
                    value={versionData.version}
                    onChange={handleChange}
                    placeholder="e.g. 1.0.1"
                    variant="outlined"
                    required
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Release Notes"
                    name="notes"
                    value={versionData.notes}
                    onChange={handleChange}
                    multiline
                    rows={3}
                    placeholder="What's new?"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={versionData.forceUpdate}
                        onChange={handleChange}
                        name="forceUpdate"
                        color="primary"
                      />
                    }
                    label="Force Update"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={versionData.isActive}
                        onChange={handleChange}
                        name="isActive"
                        color="success"
                      />
                    }
                    label="Auto Activate"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={loading}
                      fullWidth
                    >
                      {loading ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        "Publish Update"
                      )}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={7}>
          <Typography
            variant="h6"
            gutterBottom
            display="flex"
            alignItems="center"
          >
            <HistoryIcon sx={{ mr: 1 }} /> Version History
          </Typography>
          <LazyTable
            data={history.slice(
              pagination.page * pagination.rowsPerPage,
              (pagination.page + 1) * pagination.rowsPerPage,
            )}
            page={pagination.page}
            rowsPerPage={pagination.rowsPerPage}
            totalRecords={history.length}
            loading={historyLoading}
            columns={columns}
            onPageChange={(e, newPage) =>
              setPagination((p) => ({ ...p, page: newPage }))
            }
            onRowsPerPageChange={(e) =>
              setPagination((p) => ({
                ...p,
                rowsPerPage: parseInt(e.target.value, 10),
                page: 0,
              }))
            }
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AppVersionManagement;
